import i18n from 'i18n-literally'
import { createSelector } from 'redux-bundler'

import { humanize } from 'inflection'

// import config from '~/src/App/config'
import createEntityBundle from '~/src/Lib/createEntityBundle'
import createLogger from '~/src/Lib/Logging'
import { EMPTY_OBJECT } from '~/src/Lib/Utils'
import { Membership as schema } from '~/src/Store/Schemas'

import { prepareData } from './shape'

const name = 'memberships'
const logger = createLogger(name)

const initialBundle = createEntityBundle({
  name,
  apiConfig: {
    prepareData,
    schema,
    snackbar: ({ status, dispatch, action }) => {
      if (action === 'fetch') return
      dispatch({
        actionCreator: 'doAddSnackbarMessage',
        // TODO: convert the humanize to use i18n
        args: [i18n`Account ${humanize(action)} ${status}ed`],
      })
    },
  },
})
const BILLING_CONTACT_KEYS = ['administer_account', 'change_subscription_payment_methods']

// The user whose membership here holds `key`, which only the Salesforce sync assigns.
const contactHolding = key => (facilityMemberships, users) => {
  const membership = Object.values(facilityMemberships).find(m => m.permissions?.includes(key))
  return users[membership?.user] ?? null
}

const bundle = {
  ...initialBundle,
  selectFacilityMemberships: createSelector(
    'selectCurrentFacility',
    'selectMemberships',
    (currentFacility, allMemberships) => {
      const { id: facilityId } = currentFacility ?? EMPTY_OBJECT
      if (!facilityId) return EMPTY_OBJECT

      return Object.values(allMemberships).reduce(
        (acc, membership) => (membership.facility === facilityId ? { ...acc, [membership.id]: membership } : acc),
        EMPTY_OBJECT,
      )
    },
  ),
  // The facility's own AP contact: buys, sees every order, and decides who else may buy.
  selectFacilityApContact: createSelector(
    'selectFacilityMemberships',
    'selectUsers',
    contactHolding('administer_account'),
  ),
  // The contract's billing contact: the only member who can change the subscription payment method.
  selectSubscriptionPaymentContact: createSelector(
    'selectFacilityMemberships',
    'selectUsers',
    contactHolding('change_subscription_payment_methods'),
  ),
  // Everyone the card names as a billing contact: a facility can split the two duties between members.
  selectFacilityBillingContacts: createSelector(
    'selectFacilityMemberships',
    'selectUsers',
    (facilityMemberships, users) => {
      const holders = Object.values(facilityMemberships)
        .filter(m => BILLING_CONTACT_KEYS.some(key => m.permissions?.includes(key)))
        .map(m => users[m.user])
        .filter(Boolean)
      return [...new Map(holders.map(user => [user.id, user])).values()].sort((a, b) => a.email.localeCompare(b.email))
    },
  ),
  doSetPhoneAsDefault:
    id =>
    async ({ store, apiFetch }) => {
      try {
        await apiFetch(`/phones/${id}/setAsDefault/`, null, { method: 'POST' })
        store.doAddSnackbarMessage(i18n`Successfully set phone as default.`)
        await store.doFetchMe()
      } catch {
        store.doAddSnackbarMessage(i18n`Failed to set phone as default.`)
      }
    },
  doResendPhoneCode:
    id =>
    async ({ store, apiFetch }) => {
      try {
        await apiFetch(`/phones/${id}/resendCode/`, null, { method: 'GET' })
        await store.doFetchMe()
      } catch {
        store.doAddSnackbarMessage(i18n`Failed to resend confirmation code.`)
      }
    },
  doCheckConfirmationCode:
    payload =>
    async ({ store, apiFetch }) => {
      try {
        await apiFetch(`/phones/${payload.id}/confirm/`, payload.code, { method: 'POST' })
        await store.doFetchMe()
        return {
          status: 200,
          message: 'Success',
        }
      } catch {
        store.doAddSnackbarMessage(i18n`Incorrect Confirmation Code`)
        return {
          status: 400,
          message: 'Failed',
        }
      }
    },
}

export default bundle
